// DualChatPanel.jsx — Kara + Lala side-by-side chat modal
// ═══════════════════════════════════════════════════════════════════════════
// ADDITIVE COMPONENT (Rule #1). Self-contained. Doesn't touch VoiceHomeTab,
// KaraWidget, or any other chat path. Renders as a modal overlay from a tiny
// "💬💬" trigger button (or programmatic open) in AmberScanner.
//
// Why this exists
// ───────────────
// • VoiceHomeTab's DualChatPanel depended on `window.__dualChatFetch` and an
//   `onVoice` callback that were never wired into the live AmberScanner tree
//   (VoiceHomeTab is no longer rendered as a tab). When users tried the dual
//   chat, send() fell through to a missing onVoice and silently did nothing.
// • This module is fully self-sufficient: it talks to `/api/chat/stream`
//   directly with the bearer token from the parent. No prop-drilling, no
//   bridge variables, no VoiceHomeTab coupling.
//
// Usage
// ─────
//   import DualChatPanel from "./DualChatPanel";
//   <DualChatPanel
//     C={C}
//     userName="Kenyon"
//     token={auth?.token}        // ML_SERVICE_TOKEN from /api/auth/login
//     open={chat2Open}
//     onClose={()=>setChat2Open(false)}
//   />
// ═══════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from "react";
import { useAgentChat } from "./agent/useAgentChat.js";
import { AgentMessageExtras } from "./agent/AgentChatBits.jsx";

/* ─────────────────────────────────────────────────────────────────────────
   1. PALETTE — mirrors VoiceHomeTab.jsx so visual continuity is preserved
   ───────────────────────────────────────────────────────────────────────── */
const DEFAULT_C = {
  bg:     "#070d1a",
  panel:  "#0d1f35",
  border: "#1e3a5f",
  text:   "#e5edf7",
  muted:  "#6b88a8",
  blue:   "#3b82f6",
  purple: "#a855f7",
  green:  "#10b981",
  amber:  "#fbbf24",
  red:    "#ef4444",
};

/* ─────────────────────────────────────────────────────────────────────────
   2. SingleChatColumn — one self-contained chat (text only; mic stays in
      the main KaraWidget to keep this module light & reliable).
   ───────────────────────────────────────────────────────────────────────── */
function SingleChatColumn({ agent, accent, C, userName, displayName, token, onToggleAgent, ownAgent, apiFetch, runUiAction, getContext }) {
  const isOwn = agent === ownAgent;
  const ownerName = agent === "Lala" ? "Lando" : "Kenyon";
  const who = displayName || userName || "trader";
  // 2026-10-09 agent-ui: same agent runtime as the floating KaraWidget (history + context + UI actions).
  const fetcher = apiFetch || ((url, opts = {}) => fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } }));
  const { messages, setMessages, busy, send: sendAgent, confirm } = useAgentChat({
    apiFetch: fetcher,
    agent: agent.toLowerCase(),
    getContext,
    runUiAction,
    initialMessages: [{
      role: "assistant",
      content: isOwn
        ? `Yo ${who} — I'm ${agent}. I can drive the dashboard: tabs, charts, indicators, drawings. Ask away.`
        : `Signed in as ${who} — ${agent} is ${ownerName}'s agent (your teammate). Only ${ownerName} can chat with ${agent}. Press ⇄ SWAP to switch back to ${ownAgent}.`,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    }],
  });
  const [input, setInput] = useState("");
  const err = null;
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  // When the column is swapped to the other agent, reset the greeting so the note is accurate.
  useEffect(() => {
    setMessages([{
      role: "assistant",
      content: isOwn
        ? `Yo ${who} — I'm ${agent}. I can drive the dashboard: tabs, charts, indicators, drawings. Ask away.`
        : `Signed in as ${who} — ${agent} is ${ownerName}'s agent (your teammate). Only ${ownerName} can chat with ${agent}. Press ⇄ SWAP to switch back to ${ownAgent}.`,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    }]);
  }, [agent]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const txt = input.trim();
    if (!txt || busy || !isOwn) return;
    setInput("");
    await sendAgent(txt);
  };

  return (
    <div style={{
      flex: 1, minWidth: 0, height: 460,
      background: `linear-gradient(180deg, ${C.panel}ee, ${C.bg}ee)`,
      border: `1px solid ${accent}66`,
      borderRadius: 12,
      display: "flex", flexDirection: "column",
      boxShadow: `0 0 22px ${accent}33, inset 0 0 20px ${accent}0a`,
      overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(90deg, ${accent}22, transparent)`,
        borderBottom: `1px solid ${accent}55`,
        padding: "10px 14px",
        display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
      }}>
        <div style={{
          width: 30, height: 30, borderRadius: "50%",
          background: `linear-gradient(135deg, ${accent}, ${C.green})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 900, color: "#070d1a",
        }}>{agent[0]}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: accent, letterSpacing: 2 }}>{agent.toUpperCase()} AI</div>
          <div style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>{!isOwn ? `${ownerName.toUpperCase()}'S AGENT · READ ONLY` : busy ? "THINKING…" : "ONLINE · TYPE TO TALK"}</div>
        </div>
        {onToggleAgent && (
          <button
            onClick={onToggleAgent}
            title={`Switch to ${agent === "Kara" ? "Lala" : "Kara"} (server enforces per-user agent — see notes)`}
            style={{
              background: C.panel,
              border: `1px solid ${accent}55`,
              color: C.muted,
              padding: "3px 8px",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 9,
              fontFamily: "inherit",
              letterSpacing: 1,
              fontWeight: 700,
            }}
          >⇄ SWAP</button>
        )}
        <div style={{
          width: 8, height: 8, borderRadius: "50%",
          background: busy ? C.amber : (err ? C.red : accent),
          animation: busy ? "dualchatpulse 0.7s infinite" : "none",
          boxShadow: `0 0 10px ${busy ? C.amber : (err ? C.red : accent)}`,
        }}/>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
        {messages.map((m, i) => {
          const isUser = m.role === "user";
          // 2026-10-03 (Hermes) — tool-badge rendering: same shape as the
          // AmberScanner ChatPanel card, with a Confirm/Cancel button for GATED
          // server-side actions (set_stake / execute_trade / activate_strategy).
          const isToolBadge = m._isToolBadge === true;
          if (isToolBadge) {
            return (
              <div key={i} style={{ display: "flex", flexDirection: "row", gap: 6, alignItems: "flex-end" }}>
                <div style={{
                  maxWidth: "88%",
                  padding: "8px 12px",
                  borderRadius: "10px 10px 10px 3px",
                  background: `${C.bg}cc`,
                  border: `1px solid ${accent}88`,
                  fontSize: 11, color: C.text, lineHeight: 1.5,
                  boxShadow: `0 0 0 1px ${accent}33`,
                }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: accent, marginBottom: 3, letterSpacing: 0.4, textTransform: "uppercase" }}>🔧 TOOL</div>
                  <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.content}</div>
                  {m._pendingTaskId && (
                    <div style={{ marginTop: 6, display: "flex", gap: 5 }}>
                      <button
                        onClick={async () => {
                          try {
                            const r = await fetch(`/api/tasks/${encodeURIComponent(m._pendingTaskId)}/confirm`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
                              body: "{}",
                            });
                            const d = await r.json().catch(() => ({}));
                            setMessages(p => [...p, { role: "assistant", content: `Tool confirm: ${r.ok ? "✅ confirmed" : "❌ " + (d.error || "failed")}`, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
                          } catch (e) {
                            setMessages(p => [...p, { role: "assistant", content: `Confirm failed: ${e.message}`, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
                          }
                        }}
                        style={{ background: accent + "22", border: `1px solid ${accent}`, color: accent, padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit" }}
                      >✅ CONFIRM</button>
                      <button
                        onClick={async () => {
                          try {
                            await fetch(`/api/tasks/${encodeURIComponent(m._pendingTaskId)}/cancel`, {
                              method: "POST",
                              headers: { "Content-Type": "application/json", ...(token ? { "Authorization": `Bearer ${token}` } : {}) },
                              body: "{}",
                            });
                            setMessages(p => [...p, { role: "assistant", content: "Cancelled the pending action.", time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]);
                          } catch (e) { /* silent */ }
                        }}
                        style={{ background: "#ef444422", border: "1px solid #ef4444", color: "#ef4444", padding: "4px 10px", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 700, fontFamily: "inherit" }}
                      >❌ CANCEL</button>
                    </div>
                  )}
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 3 }}>{m.time}</div>
                </div>
              </div>
            );
          }
          return (
            <div key={i} data-testid="chat-msg" data-role={m.role} style={{ display: "flex", flexDirection: isUser ? "row-reverse" : "row", gap: 6, alignItems: "flex-end" }}>
              <div style={{
                maxWidth: "85%",
                padding: "7px 11px",
                borderRadius: isUser ? "10px 10px 3px 10px" : "10px 10px 10px 3px",
                background: isUser ? `${C.blue}22` : `${accent}15`,
                border: `1px solid ${isUser ? C.blue + "55" : accent + "44"}`,
                fontSize: 12, color: C.text, lineHeight: 1.5,
                fontFamily: "'Courier New', monospace",
                whiteSpace: "pre-wrap", wordBreak: "break-word",
              }}>
                {m.content || (busy && !isUser && i === messages.length - 1 ? (
                  <span style={{ opacity: 0.6 }}>...</span>
                ) : "")}
                {!isUser && m._agent && <AgentMessageExtras m={m} onConfirm={confirm} accent={accent} />}
                <div style={{ fontSize: 9, color: C.muted, marginTop: 3, textAlign: isUser ? "right" : "left" }}>{m.time}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef}/>
      </div>

      {/* Input row */}
      <div style={{ padding: "8px 10px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 6, flexShrink: 0 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
          placeholder={!isOwn ? `${agent} is ${ownerName}'s agent — swap back to ${ownAgent}` : busy ? `${agent} is thinking…` : `Talk to ${agent}…`}
          disabled={busy || !isOwn}
          style={{
            flex: 1, background: C.panel, border: `1px solid ${C.border}`,
            color: C.text, padding: "7px 10px", borderRadius: 6,
            fontSize: 12, fontFamily: "inherit", outline: "none", minWidth: 0,
          }}
        />
        <button
          onClick={send}
          disabled={busy || !input.trim() || !isOwn}
          style={{
            background: (!busy && input.trim()) ? accent + "22" : C.panel,
            border: `1px solid ${(!busy && input.trim()) ? accent : C.border}`,
            color: (!busy && input.trim()) ? accent : C.muted,
            padding: "7px 11px", borderRadius: 6,
            cursor: (!busy && input.trim()) ? "pointer" : "default",
            fontSize: 14, fontFamily: "inherit", flexShrink: 0,
          }}
        >➤</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   3. DualChatPanel — modal containing both columns + close button
   ───────────────────────────────────────────────────────────────────────── */
export default function DualChatPanel({
  C = DEFAULT_C,
  userName = "Kenyon",
  displayName = null,
  token = null,
  open,
  onClose,
  apiFetch = null,        // 2026-10-09 agent-ui: AmberScanner's apiFetch (bearer token)
  runUiAction = null,     // (name, input) => Promise<result> — executes ui_* tools on the dashboard
  getContext = null,      // () => dashboard context object
}) {
  // Per-user agent isolation: ken72 → kara, lando → lala. Both columns default
  // to the user's own agent so the per-user gate (server.js:2579) doesn't 403.
  // Each column has a SWAP button that flips it to the other agent — useful as
  // a playground; a 403 means the user isn't authorized for that agent.
  const ownAgent = /lando/i.test(userName || "") ? "Lala" : "Kara";
  const [leftAgent,  setLeftAgent]  = useState(ownAgent);
  const [rightAgent, setRightAgent] = useState(ownAgent);
  const toggleLeft  = () => setLeftAgent(a  => (a === "Kara" ? "Lala" : "Kara"));
  const toggleRight = () => setRightAgent(a => (a === "Kara" ? "Lala" : "Kara"));
  const leftAccent  = leftAgent  === "Lala" ? C.purple : C.blue;
  const rightAccent = rightAgent === "Lala" ? C.purple : C.blue;

  // Inject the keyframes once (idempotent).
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById("__dual_chat_kf")) return;
    const s = document.createElement("style");
    s.id = "__dual_chat_kf";
    s.textContent = "@keyframes dualchatpulse{0%{opacity:1}50%{opacity:0.3}100%{opacity:1}}";
    document.head.appendChild(s);
  }, []);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Kara and Lala chat"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(7,13,26,0.78)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 980,
          background: `linear-gradient(180deg, ${C.bg}f8, ${C.bg}f8)`,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          padding: 16,
          boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 30px ${C.blue}22, 0 0 30px ${C.purple}22`,
        }}
      >
        {/* Modal header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 12, gap: 8,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 900, letterSpacing: 2, color: C.text }}>DUAL CHAT</div>
            <div style={{ fontSize: 10, color: C.muted, letterSpacing: 1, marginTop: 2 }}>
              Two AIs in parallel · press ESC or click outside to close
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close dual chat"
            style={{
              background: C.panel, border: `1px solid ${C.border}`,
              color: C.muted, padding: "6px 10px", borderRadius: 6,
              fontFamily: "inherit", cursor: "pointer", fontSize: 12,
            }}
          >✕ CLOSE</button>
        </div>

        {/* Side-by-side columns */}
        <div style={{
          display: "flex", flexDirection: "row", gap: 12, flexWrap: "wrap",
        }}>
          <SingleChatColumn
            agent={leftAgent}
            accent={leftAccent}
            C={C}
            userName={userName}
            token={token}
            onToggleAgent={toggleLeft}
            ownAgent={ownAgent}
            displayName={displayName}
            apiFetch={apiFetch}
            runUiAction={runUiAction}
            getContext={getContext}
          />
          <SingleChatColumn
            agent={rightAgent}
            accent={rightAccent}
            C={C}
            userName={userName}
            token={token}
            onToggleAgent={toggleRight}
            ownAgent={ownAgent}
            displayName={displayName}
            apiFetch={apiFetch}
            runUiAction={runUiAction}
            getContext={getContext}
          />
        </div>
      </div>
    </div>
  );
}
